import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Collapse,
  TextField,
  Alert,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Lock,
  Delete,
  Warning,
  ExitToApp,
  ExpandMore,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import api from "../../api";
import { FormattedMessage } from "react-intl";
import { useSnackbar } from "notistack";
import { token_keys, routes } from "../../constants";

export default function SecuritySection({ loginHistory, updateLoginHistory }) {
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleForgotPasswordConfirm = () => {
    localStorage.removeItem(token_keys.access_token);
    localStorage.removeItem(token_keys.refresh_token);
    localStorage.removeItem(token_keys.expiration);
    localStorage.removeItem(token_keys.issued_at);
    setForgotPasswordModalOpen(false);
    navigate(routes.forgotPassword);
  };

  // Pagination constants
  const ITEMS_PER_PAGE = 5;
  const totalPages = Math.ceil(loginHistory.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedLoginHistory = loginHistory.slice(startIndex, endIndex);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async () => {
    setFieldErrors({});

    if (passwordData.newPassword === passwordData.currentPassword) {
      const matchError = <FormattedMessage id="account.password_same_as_current" />;
      enqueueSnackbar(matchError, { variant: "error" });
      setFieldErrors({ newPassword: matchError });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      enqueueSnackbar(<FormattedMessage id="account.password_mismatch" />, { variant: "error" });
      setFieldErrors({ confirmPassword: <FormattedMessage id="account.password_mismatch" /> });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      enqueueSnackbar(<FormattedMessage id="account.password_length" />, { variant: "error" });
      setFieldErrors({ newPassword: <FormattedMessage id="account.password_length" /> });
      return;
    }

    try {
      const accessToken = localStorage.getItem(token_keys.access_token);
      const response = await api.updatePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword,
        access_token: accessToken,
      });

      if (response.success) {
        setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setShowPasswordFields(false);
        enqueueSnackbar(<FormattedMessage id="account.password_updated" />, { variant: "success" });
        enqueueSnackbar(<FormattedMessage id="account.redirecting_to_login" />, { variant: "info" });
        setTimeout(() => {
          localStorage.removeItem(token_keys.access_token);
          localStorage.removeItem(token_keys.refresh_token);
          localStorage.removeItem(token_keys.expiration);
          localStorage.removeItem(token_keys.issued_at);
          navigate(routes.login);
        }, 1000);
      } else {
        let errorMessage = <FormattedMessage id="account.password_update_failed" />;
        if (response.status === 400) {
          if (response.fieldErrors?.current_password) {
            errorMessage = <FormattedMessage id="account.current_password_incorrect" />;
            setFieldErrors({ currentPassword: errorMessage });
          } else {
            errorMessage = <FormattedMessage id="account.invalid_request" />;
          }
        } else if (response.status === 401) {
          errorMessage = <FormattedMessage id="account.unauthorized" />;
        } else if (response.error?.includes('weak')) {
          errorMessage = <FormattedMessage id="account.password_too_weak" />;
          setFieldErrors({ newPassword: errorMessage });
        } else if (response.status >= 500) {
          errorMessage = <FormattedMessage id="account.server_error" />;
        } else {
          errorMessage = response.error;
        }
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    } catch (error) {
      console.error("Failed to update password:", error);
      enqueueSnackbar(<FormattedMessage id="account.password_update_failed" />, { variant: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.toLowerCase() === "delete") {
      try {
        await api.deleteAccount();
        enqueueSnackbar(<FormattedMessage id="account.account_deleted" />, { variant: "success" });
        enqueueSnackbar(<FormattedMessage id="account.redirecting_to_login" />, { variant: "info" });
        setTimeout(() => {
          localStorage.removeItem(token_keys.access_token);
          localStorage.removeItem(token_keys.refresh_token);
          localStorage.removeItem(token_keys.expiration);
          navigate(routes.login);
        }, 1000);
      } catch (error) {
        console.error("Failed to delete account:", error);
        enqueueSnackbar(<FormattedMessage id="account.account_delete_failed" />, { variant: "error" });
      }
    } else {
      enqueueSnackbar(<FormattedMessage id="account.please_delete_confirm_label" />, { variant: "error" });
    }
  };

  const handleLogoutSession = async (sessionId) => {
    try {
      const response = await api.logoutActiveSession(sessionId);
      if (response.status === 200) {
        const updatedHistory = loginHistory.filter((session) => session.id !== sessionId);
        updateLoginHistory(updatedHistory);
        enqueueSnackbar(<FormattedMessage id="account.session_logout_success" />, { variant: "success" });
      }
    } catch (error) {
      console.error("Failed to logout session:", error);
      enqueueSnackbar(<FormattedMessage id="account.session_logout_failed" />, { variant: "error" });
    }
  };

  const handleLogoutAllSessions = async () => {
    const activeSessionIds = loginHistory.map((entry) => entry.id);
    if (activeSessionIds.length === 0) {
      enqueueSnackbar(<FormattedMessage id="account.no_active_sessions" />, {
        variant: "info",
        autoHideDuration: 2000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    try {
      const response = await api.logoutAllActiveSessions({ session_ids: activeSessionIds });
      if (response.status === 200) {
        const updatedHistory = loginHistory.filter((entry) => !activeSessionIds.includes(entry.id));
        console.log("Updated loginHistory after removal:", updatedHistory);
        updateLoginHistory(updatedHistory);
        enqueueSnackbar(<FormattedMessage id="account.all_sessions_logout_success" />, { variant: "success" });
      }
    } catch (error) {
      console.error("Failed to logout all sessions:", error);
      enqueueSnackbar(<FormattedMessage id="account.all_sessions_logout_failed" />, { variant: "error" });
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }} color="#00438E">
        <FormattedMessage id="account.security_settings" />
      </Typography>
      <Divider sx={{ mb: 2, borderColor: "#00438E" }} />
      <Box sx={{ mt: 3 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: "#00438E" }} />}>
            <Typography variant="subtitle1" fontWeight="bold" color="#00438E">
              <FormattedMessage id="account.update_password" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Button
              variant="contained"
              startIcon={<Lock sx={{ color: "#ffffff" }} />}
              onClick={() => setShowPasswordFields(!showPasswordFields)}
              sx={{
                mb: showPasswordFields ? 2 : 0,
                backgroundColor: "#00438E",
                color: "#ffffff",
                "&:hover": { backgroundColor: "#003A75" },
              }}
            >
              {showPasswordFields ? (
                <FormattedMessage id="account.hide_password_update" />
              ) : (
                <FormattedMessage id="account.update_password" />
              )}
            </Button>
            <Collapse in={showPasswordFields}>
              <Box sx={{ maxWidth: 400 }}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="currentPassword"
                  label={<FormattedMessage id="account.current_password" />}
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  sx={{
                    "& .MuiInputLabel-root": { color: "#00438E" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#00438E" },
                      "&:hover fieldset": { borderColor: "#4A87C8" },
                      "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                    },
                  }}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  name="newPassword"
                  label={<FormattedMessage id="account.new_password" />}
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  sx={{
                    "& .MuiInputLabel-root": { color: "#00438E" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#00438E" },
                      "&:hover fieldset": { borderColor: "#4A87C8" },
                      "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                    },
                  }}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  name="confirmPassword"
                  label={<FormattedMessage id="account.confirm_new_password" />}
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  sx={{
                    "& .MuiInputLabel-root": { color: "#00438E" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#00438E" },
                      "&:hover fieldset": { borderColor: "#4A87C8" },
                      "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                    },
                  }}
                />
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handlePasswordSubmit}
                    sx={{
                      backgroundColor: "#2e7d32",
                      "&:hover": { backgroundColor: "#1f5b2a" },
                    }}
                  >
                    <FormattedMessage id="account.save_password" />
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setPasswordData({
                        currentPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                      });
                      setShowPasswordFields(false);
                    }}
                    sx={{
                      color: "#00438E",
                      borderColor: "#00438E",
                      "&:hover": { borderColor: "#4A87C8", backgroundColor: "transparent" },
                    }}
                  >
                    <FormattedMessage id="account.cancel" />
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setForgotPasswordModalOpen(true)}
                    sx={{
                      color: "#00438E",
                      fontSize: 14,
                      "&:hover": { color: "#4A87C8", backgroundColor: "transparent" },
                    }}
                  >
                    <FormattedMessage id="login.forgot_password" />
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </AccordionDetails>
        </Accordion>

        {/* Recent Login Activity Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: "#00438E" }} />}>
            <Typography variant="subtitle1" fontWeight="bold" color="#00438E" sx={{ ml: 2 }}>
              <FormattedMessage id="account.recent_login_activity" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: { xs: 1, sm: 0 },
                  pl: 2,
                  pr: 2,
                }}
              >
                <Typography variant="body1" color="#00438E">
                  <FormattedMessage id="account.your_recent_login_sessions" />
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<ExitToApp sx={{ color: "#ffffff" }} />}
                  onClick={handleLogoutAllSessions}
                  sx={{
                    mt: { xs: 1, sm: 0 },
                    backgroundColor: "#d32f2f",
                    "&:hover": { backgroundColor: "#b71c1c" },
                  }}
                >
                  <FormattedMessage id="account.logout_all_sessions" />
                </Button>
              </Box>
              {loginHistory.length > 0 ? (
                <>
                  <List dense sx={{ width: "100%", pr: 2 }}>
                    {paginatedLoginHistory.map((entry) => (
                      <ListItem
                        key={entry.id}
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" }, // Vertical on mobile, horizontal on larger screens
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: { xs: 1, sm: 2 },
                          py: 1,
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <ListItemText
                          primary={`Logged in on ${new Date(entry.login_time).toLocaleString()}`}
                          secondary={
                            <Typography component="span" variant="body2" color="textSecondary">
                              <FormattedMessage
                                id="account.login_details"
                                values={{
                                  browser: entry.browser,
                                  device: entry.device,
                                  city: entry.city,
                                }}
                              />
                            </Typography>
                          }
                          sx={{ flex: 1, wordBreak: "break-word", mb: { xs: 0, sm: 0 } }}
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          size="medium"
                          startIcon={<ExitToApp sx={{ color: "#d32f2f" }} />}
                          onClick={() => handleLogoutSession(entry.id)}
                          sx={{
                            color: "#d32f2f",
                            borderColor: "#d32f2f",
                            width: { xs: "100%", sm: "auto" }, // Full width on mobile, auto on larger screens
                            "&:hover": { borderColor: "#b71c1c", backgroundColor: "transparent" },
                            padding: "6px 16px",
                          }}
                        >
                          <FormattedMessage id="account.logout" />
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                  {/* Pagination Controls */}
                  {loginHistory.length > ITEMS_PER_PAGE && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                        mb: 1,
                      }}
                    >
                      <IconButton
                        onClick={handlePrevPage}
                        disabled={currentPage === 0}
                        sx={{
                          color: "#00438E",
                          "&:disabled": { color: "#cccccc" },
                        }}
                      >
                        <ArrowBack />
                      </IconButton>
                      <Typography variant="body2" color="#00438E">
                        {currentPage + 1} / {totalPages}
                      </Typography>
                      <IconButton
                        onClick={handleNextPage}
                        disabled={currentPage >= totalPages - 1}
                        sx={{
                          color: "#00438E",
                          "&:disabled": { color: "#cccccc" },
                        }}
                      >
                        <ArrowForward />
                      </IconButton>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ pl: 2, pr: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    <FormattedMessage id="account.no_recent_login_activity" />
                  </Typography>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: "#00438E" }} />}>
            <Typography variant="subtitle1" fontWeight="bold" color="#00438E" sx={{ ml: 2 }}>
              <FormattedMessage id="account.delete_account" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete sx={{ color: "#d32f2f" }} />}
              onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
              sx={{
                mb: showDeleteConfirm ? 2 : 0,
                ml: 2,
                color: "#d32f2f",
                borderColor: "#d32f2f",
                "&:hover": { borderColor: "#b71c1c", backgroundColor: "transparent" },
              }}
            >
              {showDeleteConfirm ? (
                <FormattedMessage id="account.hide_delete_option" />
              ) : (
                <FormattedMessage id="account.delete_account" />
              )}
            </Button>
            <Collapse in={showDeleteConfirm}>
              <Box sx={{ maxWidth: 400, ml: 2 }}>
                <Alert
                  severity="error"
                  icon={<Warning sx={{ color: "#d32f2f" }} />}
                  sx={{
                    mb: 2,
                    backgroundColor: "#ffebee",
                    color: "#d32f2f",
                    "& .MuiAlert-icon": { color: "#d32f2f" },
                  }}
                >
                  <FormattedMessage id="account.delete_warning" />
                </Alert>
                <TextField
                  fullWidth
                  label={<FormattedMessage id="account.delete_confirm_label" />}
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  sx={{
                    "& .MuiInputLabel-root": { color: "#00438E" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#00438E" },
                      "&:hover fieldset": { borderColor: "#4A87C8" },
                      "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                    },
                  }}
                />
                <Box display="flex" gap={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteConfirm}
                    sx={{
                      backgroundColor: "#d32f2f",
                      "&:hover": { backgroundColor: "#b71c1c" },
                    }}
                  >
                    <FormattedMessage id="account.delete_permanently" />
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setDeleteConfirm("");
                      setShowDeleteConfirm(false);
                    }}
                    sx={{
                      color: "#00438E",
                      borderColor: "#00438E",
                      "&:hover": { borderColor: "#4A87C8", backgroundColor: "transparent" },
                    }}
                  >
                    <FormattedMessage id="account.cancel" />
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Forgot password confirmation modal */}
      <Dialog open={forgotPasswordModalOpen} onClose={() => setForgotPasswordModalOpen(false)} maxWidth="xs" fullWidth disableScrollLock={true}>
        <DialogTitle>
          <FormattedMessage id="account.forgot_password_confirm_title" />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage id="account.forgot_password_confirm_message" />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForgotPasswordModalOpen(false)} sx={{ color: "#00438E" }}>
            <FormattedMessage id="account.cancel" />
          </Button>
          <Button
            variant="contained"
            onClick={handleForgotPasswordConfirm}
            sx={{
              backgroundColor: "#00438E",
              "&:hover": { backgroundColor: "#4A87C8" },
            }}
          >
            <FormattedMessage id="login.forgot_password" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}